// ---------------------------------------------------------------------------
// MethodPickerDialog — Dialog with three method cards (KG, Prompt, DP-RFT).
//
// Only "Knowledge Graph" is enabled in this iteration. Prompt and DP-RFT are
// shown disabled to communicate the intended product surface.
// ---------------------------------------------------------------------------

import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Text,
  mergeClasses,
} from '@fluentui/react-components';
import { useCallback, useState } from 'react';

import type {
  MethodPickerDialogProps,
  SynthMethod,
} from './MethodPickerDialog.interface.js';
import { useStyles } from './MethodPickerDialog.style.js';

interface MethodCardProps {
  title: string;
  description: string;
  selected: boolean;
  disabled?: boolean;
  comingSoon?: boolean;
  onSelect: () => void;
}

function MethodCard({
  title,
  description,
  selected,
  disabled,
  comingSoon,
  onSelect,
}: MethodCardProps) {
  const styles = useStyles();
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      className={mergeClasses(styles.card, selected && styles.cardSelected)}
      onClick={onSelect}
      disabled={disabled}
    >
      <span className={styles.cardTitle}>{title}</span>
      <span className={styles.cardDescription}>{description}</span>
      {comingSoon === true && (
        <span className={styles.cardBadge}>Coming soon</span>
      )}
    </button>
  );
}

export function MethodPickerDialog({
  open,
  onClose,
  onContinue,
}: MethodPickerDialogProps) {
  const styles = useStyles();
  const [selected, setSelected] = useState<SynthMethod | null>(null);

  const handleClose = useCallback(() => {
    setSelected(null);
    onClose();
  }, [onClose]);

  const handleContinue = useCallback(() => {
    if (selected === null || selected !== 'kg') return;
    onContinue(selected);
    setSelected(null);
  }, [selected, onContinue]);

  return (
    <Dialog
      open={open}
      onOpenChange={(_e, data) => {
        if (!data.open) handleClose();
      }}
    >
      <DialogSurface className={styles.surface}>
        <DialogBody>
          <DialogTitle>Generate Synthetic Dataset</DialogTitle>
          <DialogContent>
            <Text block className={styles.intro}>
              Choose how you want to generate your dataset.
            </Text>
            <div className={styles.cardGrid} role="radiogroup">
              <MethodCard
                title="Knowledge Graph"
                description="Builds a semantic knowledge graph from your domain, then synthesizes realistic multi-turn conversations between multiple participants."
                selected={selected === 'kg'}
                onSelect={() => setSelected('kg')}
              />
              <MethodCard
                title="Prompt based"
                description="Describe the examples you want in plain language. A language model generates conversations directly from your prompt."
                selected={false}
                disabled
                comingSoon
                onSelect={() => undefined}
              />
              <MethodCard
                title="DP-RFT"
                description="Uses direct preference reinforcement fine-tuning to generate high-quality labeled examples from model roll-outs."
                selected={false}
                disabled
                comingSoon
                onSelect={() => undefined}
              />
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              disabled={selected !== 'kg'}
              onClick={handleContinue}
            >
              Continue
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
